/**
 * The ear's decode cache (R35/D115; D265 lifted it out of the ambience player when cues needed it fetched ahead) —
 * every asset fetched once and decoded once, and fetched AHEAD when asked.
 *
 * @remarks
 * Decoding happens on first play, never ahead: a decoded piece is raw samples, ten times its compressed size, and a
 * game's whole set of cues decoded at once would hold a hundred megabytes for sounds that may never play. Fetching
 * ahead is the expensive half that a decode cannot hide; the decode of a few seconds of Opus is a few milliseconds.
 *
 * Safari cannot decode Opus-in-WebM; on a failed decode the cache retries the same URL with an `.m4a` extension — the
 * encode step ships both. D265: having learned that once, it asks for the AAC first from then on, so a prefetch in
 * Safari fetches the file Safari will decode. The title theme is decoded at the wake, before any session asks for a
 * prefetch, so the lesson is learned before it matters.
 *
 * @packageDocumentation
 */

/** The one thing the cache asks of a context: decode some bytes. */
export interface AudioDecoder {
	decodeAudioData: (bytes: ArrayBuffer) => Promise<AudioBuffer>;
}

/** Every asset fetched once, decoded once, fetched ahead on request. */
export interface AudioBufferCache {
	/** The decoded piece at `url` — from the cache, from bytes fetched ahead, or fetched now. */
	readonly bufferFor: (decoder: AudioDecoder, url: string) => Promise<AudioBuffer>;
	/** Fetch the piece at `url` now, so its first play decodes without waiting on the network. */
	readonly prefetch: (url: string) => void;
}

/** The AAC sibling every encoded asset ships beside. */
const aacSiblingOf = (url: string): string => url.replace(/\.[a-z0-9]+$/iu, '.m4a');

/**
 * Build a decode cache over a fetcher.
 *
 * @param fetchBytes - Fetch one asset's compressed bytes
 * @returns The cache
 */
export const createAudioBufferCache = (fetchBytes: (url: string) => Promise<ArrayBuffer>): AudioBufferCache => {
	const decodedBuffers = new Map<string, Promise<AudioBuffer>>();
	// Bytes fetched ahead, by the URL they were fetched from — taken once, because a decode detaches its buffer.
	const fetchedBytes = new Map<string, Promise<ArrayBuffer>>();
	let isAacFirst = false;

	const sourceUrlOf = (url: string): string => (isAacFirst ? aacSiblingOf(url) : url);

	const bytesFor = async (url: string): Promise<ArrayBuffer> => {
		const sourceUrl = sourceUrlOf(url);
		const ahead = fetchedBytes.get(sourceUrl);

		fetchedBytes.delete(sourceUrl);

		return ahead ?? fetchBytes(sourceUrl);
	};

	return {
		bufferFor: async (decoder: AudioDecoder, url: string): Promise<AudioBuffer> => {
			const cachedBuffer = decodedBuffers.get(url);

			if (cachedBuffer !== undefined) {
				return cachedBuffer;
			}

			const decodedBuffer = bytesFor(url)
				.then(async (bytes) => decoder.decodeAudioData(bytes))
				.catch(async () => {
					// Safari's decoder declines Opus-in-WebM — the AAC sibling ships beside every asset for exactly
					// this retry, and the browser that needed it once needs it every time.
					const rescued = await decoder.decodeAudioData(await fetchBytes(aacSiblingOf(url)));

					isAacFirst = true;

					return rescued;
				});

			decodedBuffers.set(url, decodedBuffer);

			return decodedBuffer;
		},

		prefetch: (url: string) => {
			const sourceUrl = sourceUrlOf(url);

			if (decodedBuffers.has(url) || fetchedBytes.has(sourceUrl)) {
				return;
			}

			const ahead = fetchBytes(sourceUrl);

			// A prefetch that fails is retried by the play that needs it; it must not surface as an unhandled rejection.
			ahead.catch(() => undefined);
			fetchedBytes.set(sourceUrl, ahead);
		},
	};
};
